import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Stack,
  Select,
  MenuItem,
  Grid,
  Checkbox,
} from "@mui/material";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { saveVoiceUserAction } from "../AccessManger/Store/access.action";
import { swalWarningMessage } from "../../../Constant/swalMessage";
import { useDispatch } from "react-redux";
import { LoadingSlides } from "../CommonComponents/Common";
import BaseButton from "../Common/Buttons/BaseButton";

import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as DeleteIcon } from "../../../assets/Images/Delete.svg";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { v4 as uuidv4 } from "uuid";

interface PendingUpload {
  testName: string;
  file: File;
}

const EditLeadModal = ({
  formData,
  handleChange,
  setTestDone,
  testDone,
  setFormData,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showUpload, setShowUpload] = useState<boolean>(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
    } else {
      swalWarningMessage("Please select a valid PDF file");
    }
  };

  const handleAdd = () => {
    if (!testDone || !file) {
      swalWarningMessage("Please provide both test name and PDF file");
      return;
    }

    setPendingUploads((prev) => [...prev, { testName: testDone, file }]);
    setTestDone("");
    setFile(null);
  };

  const handleRemove = (index: number) => {
    const updated = [...pendingUploads];
    updated.splice(index, 1);
    setPendingUploads(updated);
  };

  const uploadHandler = async () => {
    if (pendingUploads.length === 0) {
      swalWarningMessage("No files to upload.");
      return;
    }

    setLoading(true);

    for (const item of pendingUploads) {
      const formdata = new FormData();
      formdata.append("file", item.file);
      formdata.append("voiceName", "name");
      formdata.append("consentName", "anand");
      formdata.append("TNC", "true");

      const res: any = await dispatch(saveVoiceUserAction(formdata));
      if (res?.url) {
        setFormData((prev) => ({
          ...prev,
          pdfRec: [
            ...prev.pdfRec,
            {
              id: uuidv4(),
              response: item.testName,
              url: res?.url,
              manuallyAdded: true,
            },
          ],
        }));
      }
    }

    setPendingUploads([]);
    setLoading(false);
    setShowUpload(false);
    setFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = value.split(",").map((item) => item.trim());
    handleChange({ target: { name, value: JSON.stringify(formattedValue) } });
  };
  return (
    <Box px={2}>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">First Name</Typography>
          <TextField
            name="firstName"
            value={formData?.firstName || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your first name"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>

        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Last Name</Typography>
          <TextField
            name="lastName"
            value={formData?.lastName || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your last name"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Email</Typography>
          <TextField
            name="email"
            value={formData?.email || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your email"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>

        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Phone Number</Typography>
          <TextField
            name="phoneNum"
            value={formData?.phoneNum || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your phone number"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Date Of Birth</Typography>
          <TextField
            type="date"
            name="dateOfBirth"
            value={formData?.dateOfBirth || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            sx={commonStyles.inputField}
          />
        </Box>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Date Of Surgery</Typography>
          <TextField
            type="date"
            name="dateOfSurgery"
            value={formData?.dateOfSurgery || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            sx={commonStyles.inputField}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Name Of Surgeon</Typography>
          <TextField
            type="text"
            name="nameOfSurgeon"
            value={formData?.nameOfSurgeon || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            sx={commonStyles.inputField}
            placeholder="Enter surgeon name"
          />
        </Box>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">
            Phone Of Surgeon
          </Typography>
          <TextField
            name="phoneOfSurgeon"
            value={formData?.phoneOfSurgeon || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter surgeon phone number"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">
            Email Of Surgeon
          </Typography>
          <TextField
            name="emailOfSurgeon"
            value={formData?.emailOfSurgeon || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter surgeon email"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Insurance</Typography>
          <TextField
            name="insurance"
            value={formData?.insurance || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your insurance"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">
            Pre-existing Condition
          </Typography>
          <TextField
            name="preExistingCondition"
            value={formData?.preExistingCondition || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your pre-existing condition"
            sx={commonStyles.inputField}
            inputProps={{ maxLength: 25 }}
          />
        </Box>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Tests Not Done</Typography>
          <TextField
            name="testsNotDone"
            value={JSON.parse(formData?.testsNotDone || "[]")?.join(", ") || ""}
            onChange={handleInputChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your tests not done"
            sx={commonStyles.inputField}
          />
        </Box>
      </Box>

      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Arrival Time</Typography>
          <TextField
            name="arrivalTime"
            value={formData?.arrivalTime}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                arrivalTime: e.target.value,
              }))
            }
            variant="outlined"
            fullWidth
            placeholder="Enter Arrival Time"
            sx={commonStyles.inputField}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Box>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">Procedure Time</Typography>
          <TextField
            name="procedureTime"
            value={formData?.procedureTime}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                procedureTime: e.target.value,
              }))
            }
            variant="outlined"
            fullWidth
            placeholder="Enter Procedure Time"
            sx={commonStyles.inputField}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Box>
      </Box>
      <Box display="flex" gap={2.5}>
        <Box width={1 / 2} mb={1}>
          <Typography className="font-14-gery mb-1">
            All Tests Needed
          </Typography>
          <TextField
            name="actualTests"
            value={
              Array.isArray(formData?.actualTests)
                ? formData.actualTests.join(", ")
                : formData?.actualTests
                ? JSON.parse(formData.actualTests)?.join(", ")
                : ""
            }
            onChange={handleInputChange}
            variant="outlined"
            fullWidth
            placeholder="Enter all tests needed"
            sx={commonStyles.inputField}
          />
        </Box>

        <Box width={1 / 2} gap={2.5}>
          <Box className="flex-column" mb={1}>
            <Typography className="font-14-gery mb-1">Do Not Call</Typography>
            <Select
              sx={{ height: "40px", borderRadius: "8px" }}
              name="doNotContact"
              value={String(formData?.doNotContact)}
              onChange={handleChange}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>
      <Box mb={1}>
        <Box>
          <Typography className="font-14-gery mb-1">
            Test Results Received
          </Typography>

          <Box>
            <Grid container spacing={2}>
              {formData?.pdfRec?.map((record: any) => (
                <Grid
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                  key={record?.id}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <TextField
                      name="response"
                      value={record.response || ""}
                      onChange={(e) => {
                        const updatedList = formData?.pdfRec?.map((item: any) =>
                          item.id === record.id
                            ? { ...item, response: e.target.value }
                            : item
                        );

                        setFormData((prev: any) => ({
                          ...prev,
                          pdfRec: updatedList,
                        }));
                      }}
                      variant="outlined"
                      fullWidth
                      sx={{ ...commonStyles.inputField, width: "80%" }}
                      inputProps={{ maxLength: 25 }}
                    />

                    {record?.url && (
                      <BaseButton
                        title="View PDF"
                        handleClick={() => window.open(record?.url, "_blank")}
                        styles={{ color: "#4280FF" }}
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        {!showUpload && (
          <Button
            onClick={() => setShowUpload(true)}
            sx={{ ...commonStyles.saveButton, mt: 2 }}
          >
            Upload Files
          </Button>
        )}

        {showUpload && (
          <Box my={1} className="flex-row">
            <Box className="flex-column">
              <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                  placeholder="Test Name"
                  value={testDone}
                  onChange={(e) => setTestDone(e.target.value)}
                  sx={commonStyles.inputField}
                />
                <Box>
                  <input
                    type="file"
                    accept="application/pdf"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <IconButton
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    <UploadFileIcon color={file ? "success" : "action"} />
                  </IconButton>
                </Box>

                {file && (
                  <IconButton onClick={() => setFile(null)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                )}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  sx={commonStyles.addBtn}
                >
                  Add
                </Button>
              </Stack>

              {pendingUploads?.map((item, idx) => (
                <Box
                  key={idx}
                  className="flex-between mb-1 mt-1"
                  sx={{ background: "#f5f5f5", borderRadius: 2 }}
                >
                  <Typography>
                    {item.testName} - {item.file.name}
                  </Typography>
                  <IconButton onClick={() => handleRemove(idx)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
              ))}

              <Button
                onClick={uploadHandler}
                sx={{ ...commonStyles.saveButton, mt: 2 }}
                disabled={loading || pendingUploads.length === 0}
              >
                {loading ? <LoadingSlides bgColor="white" /> : "Upload"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Box display="flex" justifyItems="center" gap={2}>
        <Typography
          className="font-14-gery mb-1"
          display="flex"
          alignItems="center"
        >
          All Tests Done
        </Typography>
        <Checkbox
          name="allTestsDone"
          checked={formData?.allTestsDone}
          onChange={handleChange}
          sx={{
            "& .MuiSvgIcon-root": {
              fontSize: 20,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default EditLeadModal;
